// The event log store: IndexedDB, append-only, one log per track (keyed by the SHA-256 of the file).
//
// Nothing here edits or deletes an event: a correction is a new event and `undo` is an `undo` event
// (core's events.ts). The count map is always derived from the log with `deriveMap`; it is never stored.
// Times in `one` and `reanchor` events are the heard song time of the tap already corrected by the calibration in force when
// it was made (the record says which device and how much: `device`, `offsetS`). Calibrating again never rewrites earlier taps.
// Records written before that (no `corrected`) hold the raw time; reading them takes their own `offsetS` off.
//
// The taps S1 and S2 stored (`taps` store: file time already corrected by the calibration, and `raw` when
// known) are read once per track and appended as `one` events (`raw ?? time`); a flag in `meta` says it was
// done and when. They stay in the `taps` store, which holds truth-mode taps: `truthTapsFor` leaves the migrated ones out.

import type { CountEvent } from '@salsacount/core';
import { EVENTS, META, committed, open, request, tapsFor, type TapEvent } from '../store.js';

/** One stored row: the event with when and where it was made. */
export interface LogRecord {
  /** Append order; assigned by the store. */
  readonly n?: number;
  readonly track: string;
  readonly event: CountEvent;
  /** Wall-clock ms. */
  readonly wall: number;
  /** The output device and the calibration offset (seconds) that was taken off the tap's time when it was made. */
  readonly device: string;
  readonly offsetS: number;
  /** true: the event's tap time already has `offsetS` taken off. Absent in records from before: the time is raw. */
  readonly corrected?: true;
}

/** A record with the tap time corrected (records written before correction was stored hold the raw time and their own offset). */
function normalised(r: LogRecord): LogRecord {
  if (r.corrected) return r;
  const e = r.event;
  const event: CountEvent = e.kind === 'one' || e.kind === 'reanchor' ? { ...e, t: e.t - r.offsetS } : e;
  return { ...r, event, corrected: true };
}

const migratedKey = (track: string) => `migrated:${track}`;

/** Append one event. */
export async function appendEvent(record: LogRecord): Promise<void> {
  const db = await open();
  try {
    const tx = db.transaction(EVENTS, 'readwrite');
    const done = committed(tx);
    tx.objectStore(EVENTS).add({ ...record, corrected: true });
    await done;
  } finally {
    db.close();
  }
}

/** The events of a track in the order they were appended. */
export async function readLog(track: string): Promise<CountEvent[]> {
  const db = await open();
  try {
    const rows = await request<LogRecord[]>(db.transaction(EVENTS, 'readonly').objectStore(EVENTS).index('track').getAll(track));
    return rows.sort((a, b) => (a.n ?? 0) - (b.n ?? 0)).map((r) => normalised(r).event);
  } finally {
    db.close();
  }
}

/**
 * The log of a track, converting the S1/S2 taps first if that has not been done (and the log is empty).
 * Throws if storage is refused; the caller then works from memory.
 */
export async function loadLog(track: string, device: string): Promise<CountEvent[]> {
  const db = await open();
  let done: unknown;
  try {
    done = await request(db.transaction(META, 'readonly').objectStore(META).get(migratedKey(track)));
  } finally {
    db.close();
  }
  let events = await readLog(track);
  if (!done) {
    if (events.length === 0) {
      const legacy = (await tapsFor(track)).map((tap) => ({ t: tap.raw ?? tap.time, wall: tap.wall })).sort((a, b) => a.t - b.t);
      for (const { t, wall } of legacy) {
        await appendEvent({ track, event: { kind: 'one', t }, wall, device, offsetS: 0 });
      }
      events = await readLog(track);
    }
    const db2 = await open();
    try {
      const tx = db2.transaction(META, 'readwrite');
      const wrote = committed(tx);
      tx.objectStore(META).put({ key: migratedKey(track), at: Date.now() });
      await wrote;
    } finally {
      db2.close();
    }
  }
  return events;
}

/**
 * The truth-mode taps of a track: the `taps` store minus the S1/S2 count taps that were copied into the log (they were made before
 * the track's migration flag was written, so they are older than it). Counting or exporting them as truth would contaminate the benchmark.
 */
export async function truthTapsFor(track: string): Promise<TapEvent[]> {
  const db = await open();
  let at = 0;
  try {
    const flag = await request<{ at?: number } | undefined>(db.transaction(META, 'readonly').objectStore(META).get(migratedKey(track)));
    at = flag?.at ?? 0;
  } finally {
    db.close();
  }
  return (await tapsFor(track)).filter((t) => t.wall >= at && !t.removed);
}

/** The stored records of a track in append order (with when, and on which device): what an export writes. */
export async function readRecords(track: string): Promise<LogRecord[]> {
  const db = await open();
  try {
    const rows = await request<LogRecord[]>(db.transaction(EVENTS, 'readonly').objectStore(EVENTS).index('track').getAll(track));
    return rows.sort((a, b) => (a.n ?? 0) - (b.n ?? 0)).map(normalised);
  } finally {
    db.close();
  }
}

/** Every stored event record of every track, grouped by track in append order (what "Export all my counts" writes). */
export async function readAllRecords(): Promise<Map<string, LogRecord[]>> {
  const db = await open();
  try {
    const rows = await request<LogRecord[]>(db.transaction(EVENTS, 'readonly').objectStore(EVENTS).getAll());
    const by = new Map<string, LogRecord[]>();
    for (const r of rows.sort((a, b) => (a.n ?? 0) - (b.n ?? 0)).map(normalised)) {
      const list = by.get(r.track);
      if (list) list.push(r); else by.set(r.track, [r]);
    }
    return by;
  } finally {
    db.close();
  }
}

/**
 * Delete everything saved for a track: its event log, the flag that its S1/S2 taps were converted, and its truth taps.
 * This is the one place the log is ever deleted from, and only after the dancer confirmed it (library.ts).
 */
export async function deleteTrackData(track: string): Promise<{ events: number; truth: number }> {
  const db = await open();
  try {
    const tx = db.transaction([EVENTS, META, 'taps'], 'readwrite');
    const events = tx.objectStore(EVENTS);
    const taps = tx.objectStore('taps');
    const eventKeys = await request(events.index('track').getAllKeys(track));
    const tapKeys = await request(taps.index('track').getAllKeys(track));
    for (const k of eventKeys) events.delete(k);
    for (const k of tapKeys) taps.delete(k);
    tx.objectStore(META).delete(migratedKey(track));
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
    return { events: eventKeys.length, truth: tapKeys.length };
  } finally {
    db.close();
  }
}
